import styles from './styles/PlanTable.css'
import { useLocation } from 'react-router-dom'
import PlanData from '../../../config/static/pricing-plans'
import Button from '../forms/Button'
import { toggleModal } from '../../store/ui/actions'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import CircleSpinner from '../misc/CircleSpinner'
import * as analytics from 'lib/analytics'
import Card from '../layout/Card'
import { getLocalCurrencyAmounts } from '../../lib/get-local-price'
import { setMode } from '../../store/login-modal/actions'
import InfoBanner from 'components/forms/InfoBanner'
import Link from 'components/misc/Link'
import { getUserCountry } from 'lib/util'
import classNames from 'classnames'
import Toggle from '../forms/Toggle'

const getPriceID = (annual) => {
  return annual ? CONFIG.stripe.premiumPlanAnnuallyPriceID : CONFIG.stripe.premiumPlanMonthlyPriceID
}

const parseFloatFromCurrencyString = (localedPrice) => {
  const parsed = localedPrice.match(/[0-9.]+/)
  if (!parsed) {
    return null
  }
  return Number.parseFloat(parsed[0]) || null
}

const PlanTable = () => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const userIpLocationData = useSelector(({ ui }) => ui.ipLocationData.data)
  const fetchingUser = useSelector(({ ui }) => ui.fetchingUser)
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState()
  const [annualPriceMonthly, setAnnualPriceMonthly] = useState()
  const [monthlyPrice, setMonthlyPrice] = useState()
  const [, setAnnualDiscountPct] = useState('')
  const [pricingYearly, setpricingYearly] = useState(false)
  const [couponID, setCouponID] = useState('')
  const [hideToggle, setHideToggle] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    // for whatever reason corey always gets this on sign up
    const coupon = urlParams.get('coupon')?.replace('#', '')?.toUpperCase()
    if (!coupon) return

    const couponCfg = CONFIG.stripeCoupon[coupon] || null
    if (couponCfg) {
      setCouponID(coupon)
    }
    if (couponCfg?.annualOnly) {
      setpricingYearly(true)
    }
  }, [location])

  useEffect(() => {
    if (!userIpLocationData) return
    const couponPctOff = CONFIG.stripeCoupon?.[couponID]?.pctDiscount || 0

    const { currency, monthly, annualMonthly } = getLocalCurrencyAmounts({ ipLocationData: userIpLocationData, couponPctOff })
    setMonthlyPrice(monthly)
    setAnnualPriceMonthly(annualMonthly)
    setCurrency(currency)
  }, [userIpLocationData, couponID])

  useEffect(() => {
    if (!monthlyPrice || !annualPriceMonthly) {
      return
    }
    const monthlyPriceNumeric = parseFloatFromCurrencyString(monthlyPrice)
    const annualMonthlyPriceNumeric = parseFloatFromCurrencyString(annualPriceMonthly)
    if (!monthlyPriceNumeric || !annualMonthlyPriceNumeric) {
      return
    }

    const pctDiscount = `${(((monthlyPriceNumeric - annualMonthlyPriceNumeric) / (monthlyPriceNumeric)) * 100).toFixed(0)}%!`
    setAnnualDiscountPct(pctDiscount)
  }, [monthlyPrice, annualPriceMonthly])

  const handleRemoveCoupon = () => {
    setCouponID(null)
    if (hideToggle) {
      setHideToggle(false)
    }
  }

  const handleClick = (plan, annual) => (e) => {
    if (!user && !fetchingUser) {
      e.preventDefault()
      dispatch(setMode('register'))
      dispatch(toggleModal('login'))
      return
    }
    setLoading(true)

    // Since users will be pressing the back button
    // and that won't trigger normal mounting/unmounting behaviour
    // we need to clear this on timeout...
    setTimeout(() => {
      setLoading(false)
    }, 5000)

    analytics.event({
      category: 'User',
      action: 'View Checkout',
      label: plan
    })

    const planInfo = PlanData[plan]

    analytics.rawGtag('event', 'view_item', {
      currency: 'USD',
      value: parseFloat(annual ? planInfo.annualPrice : planInfo.monthlyPrice) || 0,
      items: [{
        name: plan + ` ${annual ? 'annual' : 'monthly'}`,
        price: parseFloat(annual ? planInfo.annualPrice : planInfo.monthlyPrice) || 0,
        quantity: 1,
        currency: 'USD'
      }]
    })
  }

  const couponMsg = CONFIG.stripeCoupon?.[couponID]?.bannerCopy || ''
  const couponAppliedSkipTrial = CONFIG.stripeCoupon?.[couponID]?.skipTrial || false

  let checkoutMethod = 'POST'
  let checkoutURL = CONFIG.stripe.checkoutSessionURL
  let cta = 'Upgrade now'
  if (user?.subscription?.customerID && (user?.subscription?.status === 'active' || user?.subscription?.status === 'trialing')) {
    checkoutURL = '/users/me/subscription'
    cta = 'Manage plan'
    checkoutMethod = 'GET'
  } else if (!user || (user?.subscription?.plan === 'free' && !user?.activatedTrial && !couponAppliedSkipTrial)) {
    checkoutURL = CONFIG.stripe.trialCreationURL
    cta = `Start free ${CONFIG.trialLength}-day trial`
  }
  const showCharlesworthBanner = getUserCountry() === 'China'
  let rewardfulID = ''
  // check if window.Rewardful.referral is on window
  if (typeof window !== 'undefined' && window?.Rewardful?.referral) {
    rewardfulID = window.Rewardful.referral
  }

  return (
    <div className={styles.planTableContainer}>
      {!hideToggle && (
        <div className={styles.toggleContainer}>
          <span onClick={() => setpricingYearly(false)} className={classNames(styles.label, !pricingYearly ? styles.selected : '')}>billed monthly</span>
          <Toggle
            onChange={() => setpricingYearly(!pricingYearly)}
            checked={pricingYearly}
          />
          <span onClick={() => setpricingYearly(true)} className={classNames(styles.label, pricingYearly ? styles.selected : '')}>billed yearly (save 40%)</span>
        </div>
      )}
      {couponID && (
        <InfoBanner state='success' className={styles.couponContainer}>
          <span className={styles.bannerCopy}>{couponMsg}</span>
          <span onClick={handleRemoveCoupon} className={styles.bannerCta}>Remove</span>
        </InfoBanner>
      )}

      <div className={styles.plansContainer}>
        <Card level={3} className={styles.planContainer}>
          <div className={styles.header}>Plus</div>
          <div className={styles.subheading}>
            for individual researchers & students
          </div>

          <div className={styles.flexCenter}>
            <div className={styles.price}>{pricingYearly ? annualPriceMonthly : monthlyPrice}</div>
            <div className={styles.billed}>
              per month {pricingYearly ? 'billed yearly' : ''}
            </div>
          </div>

          <form action={checkoutURL} method={checkoutMethod}>
            <input type='hidden' name='priceId' value={getPriceID(pricingYearly)} />
            <input type='hidden' name='plan' value='premium' />
            <input type='hidden' name='annual' value={pricingYearly} />
            <input type='hidden' name='currency' value={currency} />
            <input type='hidden' name='rewardfulID' value={rewardfulID} />
            {couponID && <input type='hidden' name='coupon' value={couponID} />}
            <Button
              className={styles.cta}
              color='blue'
              onClick={handleClick('premium', pricingYearly)}
              bold
            >
              {loading
                ? <CircleSpinner size={10} color='white' />
                : cta}
              <i className={styles.rightArrow} />
            </Button>
          </form>

          <hr className={classNames(styles.hr, styles.opacity5)} />
          <div className={styles.features}>
            • Unlimited Assistant chats
          </div>

          <div className={styles.features}>
            • Unlimited search usage
          </div>

          <div className={styles.features}>
            • Reference Check
          </div>

          <div className={styles.features}>
            • Custom Dashboards
          </div>

          <div className={styles.features}>
            • Citation & retraction alerts
          </div>

        </Card>

        <Card level={3} className={classNames(styles.planContainer, styles.bgLightBlue)}>
          <div className={styles.header}>Institution</div>
          <div className={styles.subheading}>
            for innovative research labs and Universities
          </div>

          <div className={styles.center}>
            <div className={styles.price}>Custom Pricing</div>
          </div>

          <Link
            bold
            blue
            to='/contact-sales'
          >
            <Button
              className={styles.cta}
              color='blue'
            >
              Get a free quote
            </Button>
          </Link>

          <hr className={classNames(styles.hr, styles.opacity5)} />
          <div className={styles.features}>
            • Access for students and/or staff
          </div>

          <div className={styles.features}>
            • Email-based access
          </div>

          <div className={styles.features}>
            • Volume-based discounts
          </div>

          <div className={styles.features}>
            • Centralized billing & admin
          </div>

          <div className={styles.features}>
            • Enhanced support & training
          </div>

        </Card>
      </div>

      <div className={classNames(styles.pricingSubheading, styles.margin1)}>
        <div className={styles.features}>
          After your free trial ends, you'll be automatically enrolled in your chosen plan. You can change or cancel your subscription during your trial period.
        </div>
      </div>

      {showCharlesworthBanner && (
        <InfoBanner state='info' className={styles.charlesworthBanner}>
          <span>
            It looks like you're based in China; to make it easier, you can also purchase an annual scite subscription <Link href='https://www.cwauthors.com.cn/Scite_' underline blue>from our distribution partner, The Charlesworth Group</Link>.
          </span>
        </InfoBanner>
      )}
    </div>
  )
}

export default PlanTable
